import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  PATIENT_DOCUMENT_ACCEPT,
  PATIENT_DOCUMENT_CATEGORIES,
  PATIENT_DOCUMENT_MAX_BYTES,
  deletePatientDocument,
  formatFileSize,
  getPatientDocumentOpenUrl,
  listPatientDocuments,
  releasePatientDocumentPreview,
  stagePatientDocument,
  uploadPatientDocument,
} from '../services/patientDocumentsService'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import PatientCollapsibleSection from './PatientCollapsibleSection'
import {
  PATIENT_FORM_TOKENS as T,
  PatientFieldHint,
  PatientNativeSelect,
  PatientRowDeleteButton,
} from './PatientFormPrimitives'

const iconSx = { fontSize: '18px', color: T.muted }

function FileTypeIcon({ type }) {
  if (typeof type === 'string' && type.startsWith('image/')) return <ImageOutlinedIcon sx={{ fontSize: '20px', color: T.accent }} />
  if (type === 'application/pdf') return <PictureAsPdfOutlinedIcon sx={{ fontSize: '20px', color: '#b91c1c' }} />
  return <DescriptionOutlinedIcon sx={{ fontSize: '20px', color: T.muted }} />
}

function ActionButton({ icon, children, onClick, primary = false, disabled = false }) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        minHeight: { xs: '44px', sm: '38px' },
        padding: '0 14px',
        borderRadius: '8px',
        fontSize: '13.5px',
        fontWeight: 700,
        color: primary ? '#fff' : '#1e293b',
        background: primary ? T.accent : T.surface,
        border: primary ? 'none' : '1px solid #cbd5e1',
        flex: { xs: '1 1 auto', sm: '0 0 auto' },
        opacity: disabled ? 0.6 : 1,
        '&:hover': { background: primary ? '#3f4fc9' : T.surfaceSoft },
        '&.Mui-focusVisible': { boxShadow: `0 0 0 3px ${T.accentRing}` },
      }}
    >
      {icon}
      {children}
    </ButtonBase>
  )
}

function DocumentRow({ name, size, type, previewUrl, category, onCategoryChange, status, error, onRemove, openUrl, uploadedByName, editMode }) {
  const { copy } = usePatientFormI18n()
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 10px',
        borderRadius: '10px',
        border: `1px solid ${error ? '#fca5a5' : '#e2e8f0'}`,
        background: error ? '#fff5f5' : T.surface,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          width: '44px',
          height: '44px',
          borderRadius: '8px',
          flexShrink: 0,
          overflow: 'hidden',
          background: T.surfaceSoft,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {previewUrl ? (
          <Box component="img" src={previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <FileTypeIcon type={type} />
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography component="div" sx={{ fontSize: '13.5px', fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </Typography>
        <Typography component="div" sx={{ fontSize: '12px', color: error ? T.error : T.muted }}>
          {error
            ? error
            : status === 'uploading'
              ? copy.documentsUploading
              : [formatFileSize(size), uploadedByName].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      {onCategoryChange && editMode && (
        <Box sx={{ width: { xs: '112px', sm: '130px' }, flexShrink: 0 }}>
          <PatientNativeSelect
            aria-label={copy.documentsCategoryLabel}
            value={category || 'OTHER'}
            onChange={(event) => onCategoryChange(event.target.value)}
            options={PATIENT_DOCUMENT_CATEGORIES}
            sx={{ height: { xs: '36px', sm: '32px' }, lineHeight: { xs: '34px', sm: '30px' }, fontSize: { xs: '13px', sm: '12.5px' }, padding: '0 28px 0 10px' }}
          />
        </Box>
      )}
      {status === 'uploading' && <CircularProgress size={18} thickness={5} sx={{ color: T.accent, flexShrink: 0 }} />}
      {openUrl && (
        <ButtonBase
          component="a"
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={copy.documentsOpen}
          sx={{ width: '36px', height: '36px', borderRadius: '8px', color: T.accent, flexShrink: 0, '&:hover': { background: T.accentSoft } }}
        >
          <OpenInNewIcon sx={{ fontSize: '18px' }} />
        </ButtonBase>
      )}
      {onRemove && editMode && status !== 'uploading' && (
        <PatientRowDeleteButton label={copy.removeLabel} onClick={onRemove} sx={{ flexShrink: 0 }} />
      )}
    </Box>
  )
}

/**
 * Documents attached to the patient (ID proof, old reports, referrals).
 * - Creating: files are staged locally (with previews) and uploaded right after the patient
 *   is created, so nothing is stored for a patient that never gets saved.
 * - Existing patient: uploads go straight to the server and the live list is shown.
 * "Take picture" opens the device camera on phones (capture="environment").
 */
export default function PatientDocumentsSection({
  patientDetails,
  editMode = true,
  formHook,
  alwaysOpen = false,
}) {
  const { copy } = usePatientFormI18n()
  const patientId = patientDetails?.id
  const pending = Array.isArray(patientDetails?.pendingDocuments) ? patientDetails.pendingDocuments : []
  const [existing, setExisting] = useState([])
  const [loadingExisting, setLoadingExisting] = useState(false)
  const [uploading, setUploading] = useState({})
  const fileInputRef = useRef(null)
  const cameraInputRef = useRef(null)

  const refreshExisting = useCallback(async () => {
    if (!patientId) return
    setLoadingExisting(true)
    try {
      setExisting(await listPatientDocuments(patientId))
    } finally {
      setLoadingExisting(false)
    }
  }, [patientId])

  useEffect(() => {
    refreshExisting()
  }, [refreshExisting])

  useEffect(
    () => () => {
      pending.forEach(releasePatientDocumentPreview)
    },
    // release previews only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean)
    if (files.length === 0) return
    const tooLarge = files.filter((file) => file.size > PATIENT_DOCUMENT_MAX_BYTES)
    tooLarge.forEach((file) => toast.error(`${file.name}: ${copy.documentsTooLarge}`))
    const accepted = files.filter((file) => file.size <= PATIENT_DOCUMENT_MAX_BYTES)
    if (accepted.length === 0) return

    if (!patientId) {
      formHook.addPendingDocuments(accepted.map((file) => stagePatientDocument(file)))
      return
    }

    for (const file of accepted) {
      const key = `${file.name}-${file.size}-${Date.now()}`
      setUploading((prev) => ({ ...prev, [key]: { name: file.name, size: file.size, type: file.type } }))
      try {
        await uploadPatientDocument({ patientId, file, category: 'OTHER' })
      } catch (error) {
        toast.error(`${file.name}: ${copy.documentsUploadFailed}`)
      } finally {
        setUploading((prev) => {
          const next = { ...prev }
          delete next[key]
          return next
        })
      }
    }
    refreshExisting()
  }

  const handleDelete = async (document) => {
    try {
      await deletePatientDocument(document.id)
      setExisting((prev) => prev.filter((doc) => doc.id !== document.id))
    } catch (error) {
      toast.error(copy.documentsUploadFailed)
    }
  }

  const count = pending.length + existing.length
  const summary = [...existing.map((doc) => doc.fileName), ...pending.map((doc) => doc.name)].slice(0, 3).join(', ')

  return (
    <PatientCollapsibleSection
      id="patient-documents-section"
      title={copy.documentsSectionTitle}
      icon={<FolderOutlinedIcon sx={iconSx} />}
      summary={summary}
      count={count}
      countLabel={count > 0 ? `${count}` : ''}
      defaultExpanded={count > 0}
      alwaysOpen={alwaysOpen}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {editMode && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={PATIENT_DOCUMENT_ACCEPT}
              data-testid="patient-documents-file-input"
              onChange={(event) => {
                handleFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              hidden
              accept="image/*"
              capture="environment"
              data-testid="patient-documents-camera-input"
              onChange={(event) => {
                handleFiles(event.target.files)
                event.target.value = ''
              }}
            />
            <ActionButton primary icon={<UploadFileOutlinedIcon sx={{ fontSize: '18px' }} />} onClick={() => fileInputRef.current?.click()}>
              {copy.documentsUploadButton}
            </ActionButton>
            <ActionButton icon={<CameraAltOutlinedIcon sx={{ fontSize: '18px' }} />} onClick={() => cameraInputRef.current?.click()}>
              {copy.documentsTakePictureButton}
            </ActionButton>
          </Box>
        )}

        {loadingExisting && existing.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' }}>
            <CircularProgress size={16} thickness={5} sx={{ color: T.accent }} />
          </Box>
        )}

        {existing.map((doc) => (
          <DocumentRow
            key={doc.id}
            name={doc.fileName}
            size={doc.sizeBytes}
            type={doc.mimeType}
            uploadedByName={doc.uploadedByName}
            openUrl={getPatientDocumentOpenUrl(doc)}
            onRemove={editMode ? () => handleDelete(doc) : undefined}
            editMode={editMode}
          />
        ))}

        {Object.entries(uploading).map(([key, file]) => (
          <DocumentRow key={key} name={file.name} size={file.size} type={file.type} status="uploading" editMode={editMode} />
        ))}

        {pending.map((doc) => (
          <DocumentRow
            key={doc.localId}
            name={doc.name}
            size={doc.size}
            type={doc.type}
            previewUrl={doc.previewUrl}
            category={doc.category}
            onCategoryChange={(category) => formHook.updatePendingDocument(doc.localId, { category })}
            status={doc.status}
            error={doc.error}
            onRemove={() => {
              releasePatientDocumentPreview(doc)
              formHook.removePendingDocument(doc.localId)
            }}
            editMode={editMode}
          />
        ))}

        {count === 0 && Object.keys(uploading).length === 0 && !loadingExisting && (
          <PatientFieldHint sx={{ marginTop: 0 }}>{copy.documentsEmpty}</PatientFieldHint>
        )}
        {!patientId && pending.length > 0 && (
          <PatientFieldHint sx={{ marginTop: 0 }}>{copy.documentsPendingHint}</PatientFieldHint>
        )}
      </Box>
    </PatientCollapsibleSection>
  )
}
